import type { ApiEndpoint, ApiUrl, ApiResponse, ApiBody, ApiMethod, ApiEndpointsByMethod } from '../../../shared/api/contracts'
import { getInitData } from '../telegram'
import { getApiWorkspaceId, getApiWorkspaceVersion, subscribeApiWorkspace } from './workspaceState'
import { requestAborted } from '../lib/requestScope'
import { authExpiredEvent, getBrowserCsrfToken } from '../lib/browserAuth'

type ApiRequestOptions = Omit<RequestInit, 'body' | 'headers' | 'method'> & {
  method?: string
  headers?: Record<string, string>
  body?: unknown
  /** Only actor-scoped settings may outlive an in-app budget switch. */
  workspaceBound?: boolean
}

async function readErrorBody(res: Response) {
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const json = await res.json().catch(() => null)
    if (json && typeof json === 'object') return JSON.stringify(json)
  }
  return await res.text().catch(() => '')
}

type ContractRequestOptions<K extends ApiEndpoint> = Omit<ApiRequestOptions, 'method' | 'body'>
  & (ApiMethod<K> extends 'GET' ? { method?: 'GET' } : { method: ApiMethod<K> })
  & ([ApiBody<K>] extends [undefined] ? { body?: undefined } : { body: ApiBody<K> })
type ContractRequestArgs<K extends ApiEndpoint> = ApiMethod<K> extends 'GET'
  ? [options?: ContractRequestOptions<K>] : [options: ContractRequestOptions<K>]
type ContractBodyArgs<K extends ApiEndpoint> = [ApiBody<K>] extends [undefined]
  ? [body?: undefined, signal?: AbortSignal] : [body: ApiBody<K>, signal?: AbortSignal]
// The unchecked signature is confined to the four transport adapters below.
type TransportRequest = <T>(path: string, options?: ApiRequestOptions) => Promise<T>

export function apiRequest<K extends ApiEndpoint>(path: ApiUrl<K>, ...options: ContractRequestArgs<K>): Promise<ApiResponse<K>>
export async function apiRequest(path: string, options: ApiRequestOptions = {}): Promise<unknown> {
  const { workspaceBound = true, ...fetchOptions } = options
  const version = getApiWorkspaceVersion()
  const controller = options.signal ? null : new AbortController()
  const signal = options.signal || controller!.signal
  const unsubscribe = controller && workspaceBound ? subscribeApiWorkspace(() => {
    if (version !== getApiWorkspaceVersion()) controller.abort()
  }) : () => { }
  const check = () => {
    if (signal.aborted || workspaceBound && version !== getApiWorkspaceVersion()) throw requestAborted()
  }
  try {
    check()
    const initData = getInitData()
    const method = options.method || 'GET'

    const headers: Record<string, string> = {
      'X-TG-INIT-DATA': initData,
      ...(getBrowserCsrfToken() ? { 'X-CSRF-Token': getBrowserCsrfToken()! } : {}),
      // ngrok free domains may return an HTML warning page to browser-like requests.
      // API calls must always reach Express and return JSON.
      'ngrok-skip-browser-warning': 'true',
      ...(options.headers || {}),
    }
    const ws = getApiWorkspaceId()
    if (ws && !headers['X-Workspace-Id'] && !headers['x-workspace-id']) {
      headers['X-Workspace-Id'] = ws
    }

    let body: BodyInit | undefined = undefined
    if (options.body !== undefined) {
      if (options.body instanceof ArrayBuffer) {
        body = options.body
      } else if (options.body instanceof FormData) {
        body = options.body
        // do not set content-type for FormData (browser will set boundary)
      } else if (typeof options.body === 'string') {
        body = options.body
        if (!headers['Content-Type']) headers['Content-Type'] = 'text/plain;charset=UTF-8'
      } else {
        body = JSON.stringify(options.body)
        if (!headers['Content-Type']) headers['Content-Type'] = 'application/json'
      }
    }

    const res = await fetch(path, {
      ...fetchOptions,
      method,
      headers,
      body,
      signal,
      credentials: 'same-origin',
      cache: 'no-store',
    })
    check()

    if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event(authExpiredEvent))
      const errBody = await readErrorBody(res)
      if (res.status === 403 && /app_access_denied/.test(errBody)) window.dispatchEvent(new Event(authExpiredEvent))
      check()
      if (res.status === 401 && /init_data_expired|invalid_auth_date|auth_date_in_future/.test(errBody)) {
        throw new Error('Сессия Telegram истекла. Закройте Mini App и откройте его заново через бота.')
      }
      if (res.status === 409 && /record_changed/.test(errBody)) {
        throw new Error('Запись уже изменена. Обновите страницу и проверьте актуальные данные перед повторным сохранением.')
      }
      if (res.status === 428) throw new Error('Обновите страницу: для сохранения нужна актуальная версия записи.')
      throw new Error(`HTTP ${res.status} ${errBody}`.trim())
    }

    // allow endpoints that respond with 204/empty
    if (res.status === 204) return undefined

    const contentType = res.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      const text = await res.text().catch(() => '')
      check()
      throw new Error(`Expected JSON, got ${contentType || 'unknown content-type'} ${text.slice(0, 120)}`.trim())
    }

    const result: unknown = await res.json()
    check()
    return result
  } catch (error) {
    check()
    throw error
  } finally { unsubscribe() }
}

export function apiGet<K extends ApiEndpointsByMethod<'GET'>>(path: ApiUrl<K>, signal?: AbortSignal): Promise<ApiResponse<K>>
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return (apiRequest as TransportRequest)<T>(path, { method: 'GET', signal })
}

export function apiPost<K extends ApiEndpointsByMethod<'POST'>>(path: ApiUrl<K>, ...body: ContractBodyArgs<K>): Promise<ApiResponse<K>>
export async function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return (apiRequest as TransportRequest)<T>(path, { method: 'POST', body, signal })
}

export function apiPut<K extends ApiEndpointsByMethod<'PUT'>>(path: ApiUrl<K>, ...body: ContractBodyArgs<K>): Promise<ApiResponse<K>>
export async function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return (apiRequest as TransportRequest)<T>(path, { method: 'PUT', body, signal })
}

export function apiDelete<K extends ApiEndpointsByMethod<'DELETE'>>(path: ApiUrl<K>, signal?: AbortSignal): Promise<ApiResponse<K>>
export async function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return (apiRequest as TransportRequest)<T>(path, { method: 'DELETE', signal })
}
